import {
  FilterableSurveyResult,
  SecurityCultureSurvey,
} from '@/client/security-culture/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { SemiDoughnutChart } from '@/ui/charts/semi-doughnut-chart';
import { FlexContainer } from '@/ui/styled-ui';
import { parseFloatingNumber } from '@/utils/helpers/ui.helper';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { CardInfo } from './CardInfo';
import { SCLegend } from './SCLegend';

const StyledCardContainer = styled(FlexContainer)<{
  isV2?: boolean;
  isMainDashboard?: boolean;
}>`
  ${({ isV2 }) =>
    isV2
      ? css`
          flex: 1 0 33%;
        `
      : css`
          grid-area: sc;
        `}
  width: 100%;
  min-width: min-content;
  background-color: ${({ isV2 }) =>
    isV2 ? 'var(--white-main)' : 'var(--beige-main)'};
  padding: var(--default-padding) var(--big-padding);
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;

  &.stretch-children {
    .children {
      overflow-y: hidden;
      overflow-x: auto;
      padding-bottom: var(--medium-padding);

      > div {
        flex: 1;

        > div {
          width: 100%;
        }
      }
    }
  }

  .sc-link {
    cursor: pointer;
    &:hover {
      color: var(--purple-text);
    }
  }
`;

const StyledValueSpan = styled.span<{ isMainDashboard?: boolean }>`
  font-size: ${({ isMainDashboard }) =>
    isMainDashboard
      ? 'calc(2 * var(--medium-font-size))'
      : 'var(--heading-xlarge-font-size)}'};
  font-weight: 700;
  color: ${({ color }) =>
    color || 'inherit'}; // Using the prop with a fallback value
`;

const StyledValueChangeSpan = styled.span<{
  difference: number;
  isMainDashboard?: boolean;
}>`
  font-size: ${({ isMainDashboard }) =>
    isMainDashboard ? 'var(--semi-big-font-size)' : 'var(--body-font-size)'};
  font-weight: ${({ isMainDashboard }) =>
    isMainDashboard ? '500' : undefined};
  color: var(--green-main);
  color: ${({ difference }) =>
    difference > 0
      ? 'var(--green-main)'
      : difference < 0
      ? 'var(--red-main)'
      : 'black'};
  margin-top: ${({ isMainDashboard }) =>
    isMainDashboard ? 'var(--small-padding)' : undefined};
`;

type SecurityCultureScoreProps = {
  survey?: SecurityCultureSurvey;
  filterableSurvey?: FilterableSurveyResult;
  isLoading?: boolean;
  isMainDashboard?: boolean;
  isEmpty?: boolean;
  hasPreviousScore?: boolean;
  isV2?: boolean;
  onlyOneSelectedSurvey?: boolean;
};

export const SecurityCultureScore: React.FC<SecurityCultureScoreProps> = ({
  survey,
  filterableSurvey,
  isLoading = false,
  isMainDashboard = false,
  isEmpty = false,
  hasPreviousScore,
  isV2 = false,
  onlyOneSelectedSurvey = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [scoreDifference, setScoreDifference] = useState(0);

  useEffect(() => {
    let scoreDiff = 0;
    if (survey) {
      scoreDiff = Number(survey?.score) - Number(survey?.previousScore);
    } else if (filterableSurvey && onlyOneSelectedSurvey) {
      scoreDiff =
        Number(filterableSurvey?.surveyScore) -
        Number(filterableSurvey?.previousScore);
    }
    setScoreDifference(Number(scoreDiff));
  }, [survey, filterableSurvey]);

  return (
    <StyledCardContainer
      id="sc-score-card"
      direction="column"
      gap={24}
      justify="space-between"
      isV2={isV2}
      isMainDashboard={isMainDashboard}
    >
      {isLoading ? (
        <Skeleton size="100%" />
      ) : (
        <>
          <CardInfo
            id="security-culture-score"
            title={
              isMainDashboard
                ? t('security.culture')
                : t('security.culture.score')
            }
            redTitle={isEmpty ? t('security.culture.notMeasured') : undefined}
            subtitle={
              isMainDashboard && !isEmpty
                ? t('security.culture.score.subtitle')
                : undefined
            }
            tooltip={t('security.culture.score.tooltip')}
            style={isMainDashboard ? { fontSize: '14px', fontWeight: 500 } : {}}
            className={
              isMainDashboard && !isEmpty
                ? isV2
                  ? 'card-info-link v2'
                  : 'card-info-link'
                : ''
            }
            onClick={
              isMainDashboard && !isEmpty
                ? () =>
                    navigate(
                      RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_SURVEY](
                        survey?.id,
                      ),
                    )
                : undefined
            }
          />
          <FlexContainer
            direction={isV2 ? 'column' : 'row'}
            align={isV2 ? 'center' : 'flex-start'}
            justify="space-between"
            gap={isMainDashboard ? (isV2 ? 24 : 0) : 24}
          >
            <FlexContainer
              direction="column"
              align="flex-start"
              className={isMainDashboard && !isEmpty ? 'sc-link' : ''}
              onClick={
                isMainDashboard && !isEmpty
                  ? () =>
                      navigate(
                        RedirectPaths[
                          RedirectPathsEnum.SECURITY_CULTURE_SURVEY
                        ](survey?.id),
                      )
                  : undefined
              }
            >
              <StyledValueSpan isMainDashboard={isMainDashboard}>
                {parseFloatingNumber(
                  survey?.score ?? filterableSurvey?.surveyScore,
                )}
              </StyledValueSpan>
              {(survey?.score !== null ||
                filterableSurvey?.surveyScore !== null) &&
                ((hasPreviousScore && !!survey?.previousScore) ||
                  (!survey && !!filterableSurvey?.previousScore)) && (
                  <StyledValueChangeSpan
                    difference={scoreDifference}
                    isMainDashboard={isMainDashboard}
                  >
                    {scoreDifference > 0 ? '+' : scoreDifference < 0 ? '-' : ''}
                    {scoreDifference !== 0 && ' '}
                    {scoreDifference !== 0
                      ? parseFloatingNumber(Math.abs(scoreDifference))
                      : ' '}
                  </StyledValueChangeSpan>
                )}
            </FlexContainer>

            <SemiDoughnutChart
              id="sc-score"
              value={parseFloatingNumber(
                survey?.score ?? filterableSurvey?.surveyScore,
              )}
              controlValue={parseFloatingNumber(
                survey?.benchmarkScore ?? filterableSurvey?.benchmarkScore,
              )}
              valueTooltipSuffix={t('security.culture.target.group')}
              controlValueTooltipSuffix={t(
                'security.culture.other.organisations',
              )}
              isMainDashboard={isMainDashboard}
              isPurple
            />
          </FlexContainer>
          <SCLegend isMainDashboard={isMainDashboard} />
        </>
      )}
    </StyledCardContainer>
  );
};
